import classNames from 'classnames'
import React, { ElementType } from 'react'

import { CircleInfoLineIcon } from '@icons/CircleInfoLineIcon'
import { CloseIcon } from '@icons/CloseIcon'
import { IconColor } from '@icons/index'

import { Body1, Body2, Caption1 } from '@ui/Typography'
import { PageNoticeType, usePageNotice } from '@ui/v2/PageNotice/PageNoticeContext'

import { Button } from '@global/button/Button'
import IconButton from '@global/iconButton/IconButton'

import styles from './AlertNotice.module.scss'

type Weightless<T extends React.ComponentType<any>> = Omit<
  React.ComponentProps<T>,
  'medium' | 'semibold' | 'bold'
>

export interface NoteProps {
  id: string
  title?: string
  message: string
  paths?: string[]
  type: PageNoticeType
  icon?: ElementType
  size?: 'tiny' | 'small' | 'normal'
  action?: {
    label: string
    onClick: () => void
  }
  dismissType?: 'temporary' | 'session' | 'permanent'
  dismissDuration?: number
  canClose?: boolean
}

export const AlertNotice: React.FC<NoteProps> = (note) => {
  const { dismissNotice } = usePageNotice()

  const handleClose = () => {
    dismissNotice(note.id, note.dismissType || 'temporary', note.dismissDuration || 14)
  }

  const handleAction = () => {
    note.action?.onClick?.()
  }

  const iconColor = (): IconColor => {
    switch (note.type) {
      case 'danger':
        return 'textDangerMedEm'
      case 'success':
        return 'textSuccessMedEm'
      case 'info':
        return 'textInfoMedEm'
      case 'warning':
        return 'textWarningMedEm'
      default:
        return 'textMedEm'
    }
  }

  const IconComponent = note.icon ?? CircleInfoLineIcon
  const iconSize = { tiny: 20, small: 22, normal: 24 }[note.size ?? 'normal']

  const TinyTitle: React.FC<Weightless<typeof Caption1>> = (p) => <Caption1 medium {...p} />
  const TinyContent: React.FC<Weightless<typeof Caption1>> = (p) => <Caption1 {...p} />

  const SmallTitle: React.FC<Weightless<typeof Body2>> = (p) => <Body2 medium {...p} />
  const SmallContent: React.FC<Weightless<typeof Body2>> = (p) => <Body2 {...p} />

  const NormalTitle: React.FC<Weightless<typeof Body1>> = (p) => <Body1 medium {...p} />
  const NormalContent: React.FC<Weightless<typeof Body1>> = (p) => <Body1 {...p} />

  const TextComponent = {
    tiny: { Title: TinyTitle, Content: TinyContent },
    small: { Title: SmallTitle, Content: SmallContent },
    normal: { Title: NormalTitle, Content: NormalContent },
  }[note.size ?? 'normal']

  return (
    <div className={classNames(styles.note, styles[note.type])}>
      <IconComponent size={iconSize} color={iconColor()} />
      <div className={styles.innerWrapper}>
        <div className={styles.content}>
          {note.title && (
            <TextComponent.Title className={styles.title}>{note.title}</TextComponent.Title>
          )}
          <TextComponent.Content>{note.message}</TextComponent.Content>
        </div>
        {note.action && (
          <Button appearance='plain' onClick={handleAction}>
            {note.action.label}
          </Button>
        )}
      </div>

      {note.canClose && (
        <IconButton onClick={handleClose} className={classNames(styles.closeButton)}>
          <CloseIcon size={14} />
        </IconButton>
      )}
    </div>
  )
}
